import React, { useState, useEffect, ReactNode } from "react";
import { Api } from "laravel-request";
import { Url } from "finhelper";

import Layout from "#app/pages/router/Layout/Layout";
import HeaderLanding from "#app/interface/landing/header/Header";
import UnexpectedError from "./UnexpectedError";

interface ErrorInfo {
    error: Error;
    info: React.ErrorInfo;
}

interface ErrorBoundaryProps {
    children?: ReactNode;
}

const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
    const [hasError, setHasError] = useState(false);
    const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);

    useEffect(() => {
        if (errorInfo) {
            const logError = async () => {
                try {
                    const url = Url.getCurrentUrl();
                    await Api.post("error-log", "store", {
                        message: errorInfo.error.message,
                        trace: errorInfo.error.stack,
                        url,
                        info: errorInfo.info,
                    }).call();
                } catch (e) {
                    console.error("Failed to log error:", e);
                }
            };
            logError();
        }
    }, [errorInfo]);

    const handleError = (error: Error, info: React.ErrorInfo) => {
        setHasError(true);
        setErrorInfo({ error, info });
    };

    if (hasError) {
        return (
            <Layout>
                <HeaderLanding />
                <UnexpectedError />
            </Layout>
        );
    }

    return <>{children}</>;
};

(
    ErrorBoundary as unknown as {
        getDerivedStateFromError: (error: Error) => { hasError: boolean };
    }
).getDerivedStateFromError = () => ({
    hasError: true,
});

const ErrorBoundaryWrapper: React.FC<ErrorBoundaryProps> = (props) => (
    <ErrorBoundary {...props}>{props.children}</ErrorBoundary>
);

export default ErrorBoundaryWrapper;
